import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { addMilliseconds } from 'date-fns';

import {
    CmpsFormField,
    CmpsFormFields,
} from '../../../coaching-observation/experimental.form';
import { CommitmentSummaryItem } from '../commitment-summary-item.model';
import {
    EventParticipant,
    EventType,
    ScheduleEvent,
    ScheduleEventParticipantRole,
} from '../models/schedule-event.model';

const defaultEventDuration = 30 * 60000;

export interface ScheduleEventFormFields extends CmpsFormFields {
    EVENTID: CmpsFormField;
    EVENTTYPEID: CmpsFormField;
    PARTICIPANTS: CmpsFormField;
    COMMITMENTS: CmpsFormField;
    DATE: CmpsFormField;
    STARTTIME: CmpsFormField;
    ENDTIME: CmpsFormField;
}

const timevalmsg = "'To' time must be after 'From' time.";

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ScheduleEventFormFields: ScheduleEventFormFields = {
    EVENTID: {
        name: 'eventId',
        label: '',
    },
    EVENTTYPEID: {
        name: 'eventTypeId',
        label: '',
        placeholder: 'Please Select Event Type...',
        validators: [Validators.required],
        validationMsgs: {
            required: 'Event Type required.',
        },
    },
    COMMITMENTS: {
        name: 'commitments',
        label: '',
    },
    PARTICIPANTS: {
        name: 'participants',
        validators: [reqParticipantsValidator],
        validationMsgs: {
            reqParticipantsValidator:
                'Must have at least 1 required participant',
        },
        label: '',
    },
    DATE: {
        name: 'date',
        label: '',
        validators: [Validators.required],
        validationMsgs: {
            required: 'date is required',
            ngbDate: 'Invalid Date.',
        },
    },
    STARTTIME: {
        name: 'startTime',
        validators: [Validators.required],
        validationMsgs: {
            required: 'date is required',
            dateInvalid: 'Invalid date.',
        },
        label: '',
    },
    ENDTIME: {
        name: 'endTime',
        validators: [Validators.required],
        validationMsgs: {
            required: 'date is required',
            dateInvalid: 'Invalid date.',
            dateMin: timevalmsg,
        },
        label: '',
    },
};

function reqParticipantsValidator(
    control: AbstractControl,
): { [key: string]: boolean } | null {
    const reqPart = { reqParticipantsValidator: true };

    if (!control.value || (control.value && !control.value.length))
        return reqPart;
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    else if (control.value && control.value.length) {
        const req = control.value.filter(
            (a) =>
                a.participantRoleId ===
                ScheduleEventParticipantRole.REQUIRED_PARTICIPANT,
        );
        if (!req.length) return reqPart;
    }
    return null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function endBeforeStartValidator(
    control: AbstractControl,
): { [key: string]: boolean } | null {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const date = { endBeforeStartValidator: true };
    const formgroup = control.root as FormGroup;

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (formgroup && formgroup.controls) {
        const start = formgroup.controls.startTime;
        const end = formgroup.controls.endTime;

        if ((start.value as Date) !== null && (end.value as Date) !== null) {
            // by default, update end time to add 30 mins from start time
            const endtime = addMilliseconds(start.value, defaultEventDuration);
            formgroup.controls.endTime.setValue(endtime);
        }
    }
    return null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function endBeforeStartEndValidator(
    control: AbstractControl,
): { [key: string]: boolean } | null {
    const date = { endBeforeStartValidator: true };
    const formgroup = control.root as FormGroup;

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (formgroup && formgroup.controls) {
        const start = formgroup.controls.startTime;
        const end = formgroup.controls.endTime;

        if ((start.value as Date) !== null && (end.value as Date) !== null) {
            if (
                (end.value as Date).getTime() < (start.value as Date).getTime()
            ) {
                return date;
            }
        }
    }
    return null;
}

export interface ScheduleEventFormValue {
    eventId: number;
    eventTypeId: EventType;
    date: Date;
    participants: EventParticipant[];
    commitments: CommitmentSummaryItem[];
    startTime?: Date;
    endTime?: Date;
    eventStatusId: number;
}

export function scheduleEventFormValueReverseTransformer(
    model: ScheduleEventFormValue,
): ScheduleEvent {
    let duration = 0;
    let date = null;

    if (model.date) {
        date = new Date(model.date);

        if (model.startTime) {
            date.setHours(model.startTime.getHours());
            date.setMinutes(model.startTime.getMinutes());

            if (model.endTime) {
                // round up to the minute
                duration = Math.ceil(
                    (model.endTime.getTime() - model.startTime.getTime()) /
                        1000 /
                        60,
                );
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
        eventId: model.eventId || 0,
        eventTypeId: model.eventTypeId,
        eventDate: date,
        participants: model.participants || [],
        commitments: model.commitments
            ? model.commitments.map(
                  (c: CommitmentSummaryItem) => c.commitmentId,
              )
            : [],
        eventStatusId: model.eventStatusId,
        duration,
    } as ScheduleEvent;
}

export function scheduleEventUpdateFormValueReverseTransformer(
    model: ScheduleEventFormValue,
): ScheduleEvent {
    let duration = 0;
    let date = null;

    date = new Date(model.startTime);
    duration = Math.ceil(
        (model.endTime.getTime() - model.startTime.getTime()) / 1000 / 60,
    );

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
        eventId: model.eventId || 0,
        eventTypeId: model.eventTypeId,
        eventDate: date,
        participants: model.participants || [],
        commitments: model.commitments
            ? model.commitments.map(
                  (c: CommitmentSummaryItem) => c.commitmentId,
              )
            : [],
        eventStatusId: model.eventStatusId,
        duration,
    } as ScheduleEvent;
}
